/**
 * @fileoverview Public booking API - No authentication required
 * For landing page appointment booking functionality
 */

import axios from 'axios';
import { API_BASE_URL, STAGING_API_BASE_URL, RUNTIME_ENV } from '../config';

// Determine the API URL based on environment
const API_URL = RUNTIME_ENV === 'staging' ? STAGING_API_BASE_URL : API_BASE_URL;

// Create axios instance for public endpoints (no auth interceptors)
const publicHttp = axios.create({
  baseURL: API_URL,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

/**
 * Get list of doctors available for booking
 * @returns {Promise<Object>} List of doctors
 */
export const getPublicDoctors = async () => {
  try {
    const response = await publicHttp.get('/api/v1/public/doctors');
    return response.data;
  } catch (error) {
    console.error('Error fetching doctors:', error);
    return {
      success: false,
      data: [],
      message: error.response?.data?.message || 'Failed to fetch doctors'
    };
  }
};

/**
 * Get available slots for a doctor on a specific date
 * @param {string} doctorId - Doctor ID
 * @param {string} date - Date in YYYY-MM-DD format
 * @returns {Promise<Object>} Available slots
 */
export const getPublicDoctorSlots = async (doctorId, date) => {
  try {
    const response = await publicHttp.get(`/api/v1/public/doctors/${doctorId}/slots`, {
      params: { date }
    });
    return response.data;
  } catch (error) {
    console.error('Error fetching slots:', error);
    return {
      success: false,
      data: { slots: [] },
      message: error.response?.data?.message || 'Failed to fetch available slots'
    };
  }
};

/**
 * Submit a booking request
 * @param {Object} bookingData - Booking request data
 * @returns {Promise<Object>} Booking confirmation
 */
export const submitBookingRequest = async (bookingData) => {
  try {
    const response = await publicHttp.post('/api/v1/public/booking-request', bookingData);
    return response.data;
  } catch (error) {
    console.error('Error submitting booking request:', error);
    return {
      success: false,
      message: error.response?.data?.message || 'Failed to submit booking request'
    };
  }
};

export default {
  getPublicDoctors,
  getPublicDoctorSlots,
  submitBookingRequest
};
